import { useState, useCallback } from 'react';
import { apiService, CreateStaffRecordRequest, InviteStaffRecordRequest, RegisterStaffRequest, StaffListParams, UpdateStaffRequest } from '../services/api';
import type { BusinessStaffDto, BusinessStaffListResponse, PendingInvitationDto } from '../types/api-responses';
import { normalizeApiError } from '../types/api-errors';
// AB#1920 (WS-1b.1) — DUAL-RECOGNITION. The BE is flipping
// STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS (and siblings) to canonical registry codes
// (PR-J0). `normalizeRejectionCode` folds a canonical code back onto its legacy
// spelling so the `StaffMutationError.code` stamped below is ALWAYS the legacy
// literal — every consumer that branches on
// `code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS'` (ManageStaffSection) then
// recognises both spellings unchanged. Legacy passes through (additive).
import { normalizeRejectionCode } from '../components/QuickBookModal/rejectionMessages';

// AB#1919 (WS-5b) — staff mutations prefer the first field-level validation
// message, then the canonical AppError `detail` (7807 `detail` ?? legacy
// `message`), then the local fallback. Reads through the single normalizer so
// the 7807/legacy split is handled in one place. `errOrBody` may be either a
// raw axios error or an already-extracted `response.data` body.
function pickStaffErrorMessage(errOrBody: unknown, fallback: string): string {
    const appError = normalizeApiError(errOrBody);
    if (appError.errors && typeof appError.errors === 'object') {
        const firstKey = Object.keys(appError.errors)[0];
        const messages = firstKey ? appError.errors[firstKey] : undefined;
        if (Array.isArray(messages) && messages.length > 0 && messages[0]) {
            return messages[0];
        }
    }
    return appError.detail || fallback;
}

/**
 * AB#1862 — Structured payload the BE attaches to 422 responses from the
 * staff atomic-replace handlers (`SetStaffBranches`, `SetStaffServices`,
 * `DeleteStaff`) when removing an assignment would orphan live customer
 * appointments.
 *
 * Mirrors the `Result<T>.Failure(errors, details)` envelope from Faris's
 * AB#1862 brief §5.2. The api.ts response interceptor attaches the raw
 * `details` object onto the thrown error so hooks here can re-throw it
 * without un-typed digging.
 */
export interface StaffReassignmentConflictDetails {
    code: 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS';
    count: number;
    earliestAppointmentDate: string;
    affectedBranchIds: number[];
    affectedServiceIds: number[];
    handler: 'SetStaffBranches' | 'SetStaffServices' | 'DeleteStaff';
}

/**
 * AB#1862 — re-thrown error from the staff atomic-replace hooks. Wraps the
 * BE message + preserves the typed `code` and structured `details` so the
 * caller (ManageStaffSection) can branch on `code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS'`
 * and open the confirm dialog / block banner without re-parsing the axios shape.
 */
export interface StaffMutationError extends Error {
    code?: string;
    details?: StaffReassignmentConflictDetails;
}

/**
 * AB#1862 — narrow `unknown` to a StaffMutationError shape, copying `.code`
 * and `.details` from either the interceptor-decorated error (preferred —
 * see api.ts response interceptor) OR the raw `response.data.details` path
 * as a defensive fallback if a future hook bypasses the wrapper.
 */
function makeStaffMutationError(
    err: unknown,
    fallback: string,
): StaffMutationError {
    const axiosErr = err as {
        code?: string;
        details?: StaffReassignmentConflictDetails;
        response?: {
            data?: {
                message?: string;
                code?: string;
                errors?: Record<string, string[]>;
                details?: StaffReassignmentConflictDetails;
            };
        };
    };
    const data = axiosErr?.response?.data;
    const msg = pickStaffErrorMessage(err, fallback);
    const wrapped = new Error(msg) as StaffMutationError;
    // AB#1919 (WS-5b) — `code` via the normalizer (`errorCode ?? code`); the
    // nested AB#1862 `details.code` remains a last-resort fallback.
    const normalizedCode = normalizeApiError(err).code;
    const rawCode =
        normalizedCode !== 'UNKNOWN' ? normalizedCode : data?.details?.code;
    // AB#1920 (WS-1b.1) — fold canonical → legacy so the stamped `.code` is the
    // legacy literal whichever spelling the BE sent (dual-recognition).
    const code = rawCode ? normalizeRejectionCode(rawCode) : rawCode;
    if (code) wrapped.code = code;
    const details = axiosErr?.details ?? data?.details;
    if (details) wrapped.details = details;
    return wrapped;
}

export const useStaff = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [staff, setStaff] = useState<BusinessStaffDto[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const getBusinessStaff = useCallback(async (params?: StaffListParams): Promise<BusinessStaffListResponse> => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.getBusinessStaff(params);
            setStaff(response.staff);
            setTotalCount(response.totalCount);
            setCurrentPage(response.page);
            setTotalPages(response.totalPages);
            setPageSize(response.pageSize);
            return response;
        } catch (err: unknown) {
            const msg = pickStaffErrorMessage(err, 'Failed to fetch staff');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    // Batch B (AB#2176) — record-first staff creation + optional portal invitation. Same
    // error contract as registerStaff (message + BE `code` on the re-thrown Error).
    const wrapStaffError = (err: unknown, fallback: string): Error => {
        const appError = normalizeApiError(err);
        const msg = pickStaffErrorMessage(err, fallback);
        setError(msg);
        const code = appError.code !== 'UNKNOWN' ? appError.code : undefined;
        const wrapped = new Error(msg) as Error & { code?: string };
        if (code) wrapped.code = code;
        return wrapped;
    };

    const createStaffRecord = useCallback(async (data: CreateStaffRecordRequest): Promise<number> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.createStaffRecord(data);
        } catch (err: unknown) {
            throw wrapStaffError(err, 'Failed to add staff');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const inviteStaffRecord = useCallback(async (staffId: number, data: InviteStaffRecordRequest): Promise<number> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.inviteStaffRecord(staffId, data);
        } catch (err: unknown) {
            throw wrapStaffError(err, 'Failed to send invitation');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const registerStaff = useCallback(async (data: RegisterStaffRequest): Promise<number> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.registerStaff(data);
        } catch (err: unknown) {
            // AB#1919 (WS-5b) — message + code via the single normalizer.
            const appError = normalizeApiError(err);
            const msg = pickStaffErrorMessage(err, 'Failed to register staff');
            setError(msg);
            // AB#1845 (PBI-E) — preserve the BE error `code` (e.g.
            // STAFF_GENDER_REQUIRED) on the re-thrown Error so callers
            // can branch on it (ManageStaffSection surfaces the gender
            // miss inside the modal instead of as a toast).
            const code = appError.code !== 'UNKNOWN' ? appError.code : undefined;
            const wrapped = new Error(msg) as Error & { code?: string };
            if (code) wrapped.code = code;
            throw wrapped;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteStaff = useCallback(async (staffId: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteStaff(staffId);
        } catch (err: unknown) {
            // AB#1862 — re-throw with `.code` + `.details` preserved so the
            // section can route a `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS` 422
            // into the BookingConflictBlockBanner (no override allowed on
            // Delete) instead of a generic toast.
            const wrapped = makeStaffMutationError(err, 'Failed to delete employee');
            setError(wrapped.message);
            throw wrapped;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateStaff = useCallback(async (staffId: number, data: UpdateStaffRequest): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiService.updateStaff(staffId, data);
            // Refresh the staff list after successful update
            await getBusinessStaff();
            return result;
        } catch (err: unknown) {
            // B31 — Surface the actual BE error (FluentValidation field
            // messages OR Result<Error> shape OR plain message) instead of
            // the generic "Failed to update staff" fallback. Same pattern
            // B11 added to QuickBookModal — without it the user is stuck
            // staring at a useless toast while the real cause (e.g., email
            // already taken, FormCredential missing, validation rule) sits
            // hidden in the response body.
            // AB#1919 (WS-5b) — field-message-first, then `detail` (7807) /
            // `message` (legacy), via the single normalizer. The "Validation
            // failed" placeholder is still suppressed in favour of a field msg.
            const appError = normalizeApiError(err);
            const firstFieldMessage = appError.errors
                ? Object.values(appError.errors)
                    .map(arr => (Array.isArray(arr) ? arr[0] : null))
                    .find(m => typeof m === 'string' && m.length > 0)
                : undefined;
            const msg = (firstFieldMessage as string | undefined)
                ?? (appError.detail && appError.detail !== 'Validation failed' ? appError.detail : undefined)
                ?? 'Failed to update staff';
            setError(msg);
            // AB#1845 (PBI-E) — preserve the BE error `code` (e.g.
            // STAFF_GENDER_REQUIRED) on the re-thrown Error so the
            // staff section can route the message to the right field
            // inside the modal rather than the generic toast.
            const code = appError.code !== 'UNKNOWN' ? appError.code : undefined;
            const wrapped = new Error(msg) as Error & { code?: string };
            if (code) wrapped.code = code;
            throw wrapped;
        } finally {
            setLoading(false);
        }
    }, [getBusinessStaff]);

    // CR-2026-05-15-v3 — capability set replace (which services this staff can
    // perform). Idempotent; empty list clears all.
    //
    // AB#1862 — accepts the opt-in `forceWithActiveBookings` override flag,
    // and re-throws with `.code` + `.details` preserved so the caller can
    // branch on `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS` to open the confirm
    // dialog and (after owner acknowledges) retry with force=true.
    const setStaffServices = useCallback(async (
        staffId: number,
        serviceIds: number[],
        forceWithActiveBookings: boolean = false,
    ): Promise<boolean> => {
        try {
            return await apiService.setStaffServices(staffId, serviceIds, forceWithActiveBookings);
        } catch (err: unknown) {
            throw makeStaffMutationError(err, 'Failed to update services for this employee');
        }
    }, []);

    // CR-2026-05-16-v1 — Pending invitations: list / resend / revoke.
    const getPendingInvitations = useCallback(async (): Promise<PendingInvitationDto[]> => {
        try {
            return await apiService.getPendingInvitations();
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to load pending invitations');
            throw new Error(msg);
        }
    }, []);

    const resendInvitation = useCallback(async (invitationId: number): Promise<boolean> => {
        try {
            return await apiService.resendInvitation(invitationId);
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to resend invitation');
            throw new Error(msg);
        }
    }, []);

    const revokeInvitation = useCallback(async (invitationId: number): Promise<boolean> => {
        try {
            return await apiService.revokeInvitation(invitationId);
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to revoke invitation');
            throw new Error(msg);
        }
    }, []);

    // CR-2026-05-16-v2 — Replace branch enrollment for a staff atomically.
    //
    // AB#1862 — accepts the opt-in `forceWithActiveBookings` override flag,
    // and re-throws with `.code` + `.details` preserved so the caller can
    // branch on `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS` to open the confirm
    // dialog and (after owner acknowledges) retry with force=true.
    const setStaffBranches = useCallback(async (
        staffId: number,
        branchIds: number[],
        forceWithActiveBookings: boolean = false,
    ): Promise<boolean> => {
        try {
            return await apiService.setStaffBranches(staffId, branchIds, forceWithActiveBookings);
        } catch (err: unknown) {
            throw makeStaffMutationError(err, 'Failed to update branches for this employee');
        }
    }, []);

    // CR-2026-05-16-v1 — Staff photo upload. Returns the relative path the
    // BE saved into User.ProfilePictureUrl.
    const setStaffPhoto = useCallback(async (staffId: number, photo: File): Promise<string> => {
        try {
            return await apiService.setStaffPhoto(staffId, photo);
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to upload staff photo');
            throw new Error(msg);
        }
    }, []);

    /**
     * AB#1718 (CR-2026-05-17b) — Replace the per-branch schedule for a staff
     * member. Idempotent; empty `slots` clears the schedule.
     */
    const setStaffSchedule = useCallback(async (
        staffId: number,
        slots: import('../types/api-responses').ScheduleSlotPayload[],
    ): Promise<boolean> => {
        try {
            return await apiService.setStaffSchedule(staffId, slots);
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to update schedule for this staff');
            throw new Error(msg);
        }
    }, []);

    /**
     * AB#1718 (CR-2026-05-17b) — Read the per-branch schedule for a staff
     * member. Returns an empty `slots` array when no hours are set yet.
     */
    const getStaffSchedule = useCallback(async (
        staffId: number,
    ): Promise<import('../types/api-responses').StaffScheduleResponse> => {
        try {
            return await apiService.getStaffSchedule(staffId);
        } catch (err: unknown) {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
            const msg = pickStaffErrorMessage(data, 'Failed to load schedule for this staff');
            throw new Error(msg);
        }
    }, []);

    return {
        getBusinessStaff,
        registerStaff,
        createStaffRecord,
        inviteStaffRecord,
        deleteStaff,
        updateStaff,
        setStaffServices,
        setStaffBranches,
        getPendingInvitations,
        resendInvitation,
        revokeInvitation,
        setStaffPhoto,
        setStaffSchedule,
        getStaffSchedule,
        staff,
        totalCount,
        currentPage,
        totalPages,
        pageSize,
        loading,
        error
    };
};
